import { createSlice } from '@reduxjs/toolkit';
import { AppDispatch } from '../..';
import { IProduct, IContentProductIds } from '../../../types';
import { splitArrayIntoChunk } from '../../../utils';
import algoliasearch, { SearchIndex } from 'algoliasearch';
import { ALGOLIA_INDEX_SUFFIX } from '../../../utils/constants';

type FetchedProducts = Array<{ [key: string]: unknown }>;

export interface IProductsSlice {
  isFetching: boolean;
  loaded: boolean;
  data: IProduct[];
  error: boolean;
}

const products = createSlice({
  name: 'products',
  initialState: {
    isFetching: false,
    loaded: false,
    data: [],
    error: false
  } as IProductsSlice,
  reducers: {
    fetchStarted(state) {
      state.isFetching = true;
    },
    setProducts(state, action) {
      state.data = action.payload;
      state.isFetching = false;
      state.error = false;
      state.loaded = true;
    },
    setError(state, action) {
      state.error = action.payload;
      state.isFetching = false;
      state.data = [];
    }
  }
});

export const { fetchStarted, setProducts, setError } = products.actions;

export default products.reducer;

export const fetchProducts = (ids: IContentProductIds) => async (
  dispatch: AppDispatch
) => {
  try {
    dispatch(fetchStarted());
    const fetchedProducts = [];
    if (
      !window.GeoRestrictedResources &&
      !window.GeoRestrictedResources.GEORESTRICTED_CURRENTSITE
    ) {
      throw new Error('window.GeoRestrictedResources not available');
    }

    const algoliaClient = algoliasearch(
      process.env.REACT_APP_ALGOLIA_APP_ID,
      process.env.REACT_APP_ALGOLIA_API_KEY
    );
    const store = window.GeoRestrictedResources.GEORESTRICTED_CURRENTSITE;
    const index = `${store}_${ALGOLIA_INDEX_SUFFIX}`;
    const algoliaIndex = algoliaClient.initIndex(index);

    if (ids.variantIds.length) {
      const variantSearchResponse = await algoliaIndex.getObjects(
        ids.variantIds
      );
      fetchedProducts.push(...variantSearchResponse.results);
    }

    if (ids.masterIds.length) {
      const masterProducts = await fetchAlgoliaProductsByMasterId(
        algoliaIndex,
        ids.masterIds
      );

      fetchedProducts.push(...masterProducts);
    }

    const cleanedFetchedProducts = getArrayofUniqueObjects(fetchedProducts);

    const fetchedProductsWithRelatedVariants = getFetchedProductsWithRelatedVariants(
      cleanedFetchedProducts
    );

    dispatch(setProducts(fetchedProductsWithRelatedVariants));
  } catch (e) {
    dispatch(setError(true));
  }
};

const fetchAlgoliaProductsByMasterId = async (
  algoliaIndex: SearchIndex,
  masterIds: string[]
) => {
  const products = [];
  const chunkedIds = splitArrayIntoChunk(masterIds, 512);

  for (const ids of chunkedIds) {
    const response = await algoliaIndex.search(ids.join(','), {
      distinct: 0,
      hitsPerPage: 40
    });

    const { hits } = response;
    products.push(...hits);
  }

  return products;
};

const getArrayofUniqueObjects = (products: FetchedProducts) => {
  try {
    const productSet = new Set();
    const filteredArr = products.filter((el) => {
      let duplicate;

      if (el) {
        duplicate = productSet.has(el.id);
        productSet.add(el.id);
      }

      return el && !duplicate;
    });

    return filteredArr;
  } catch (error) {
    return null;
  }
};

const getFetchedProductsWithRelatedVariants = (products: FetchedProducts) => {
  const productsWithVariants = products.map((product) => {
    const updatedProduct = {
      ...product,
      _variants: products.filter(
        (variant) => variant.MasterID === product.MasterID
      )
    };

    return updatedProduct;
  });

  return productsWithVariants;
};
